import type { ReactNode } from 'react';

/* Building blocks shared by the feature-page scenes (FeatureScene) and the guide scenes (GuideScene). */

export type Tone = 'good' | 'warn' | 'bad' | 'info' | 'neutral';

const TONE_PILL: Record<Tone, string> = {
  good: 'bg-emerald-500/15 text-emerald-300',
  warn: 'bg-amber-500/15 text-amber-300',
  bad: 'bg-rose-500/15 text-rose-300',
  info: 'bg-sky-500/15 text-sky-300',
  neutral: 'bg-gray-500/20 text-gray-300',
};

const TONE_DOT: Record<Tone, string> = {
  good: 'bg-emerald-400',
  warn: 'bg-amber-400',
  bad: 'bg-rose-400',
  info: 'bg-sky-400',
  neutral: 'bg-gray-400',
};

export function Pill({ tone, children }: { tone: Tone; children: ReactNode }): JSX.Element {
  return (
    <span className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ${TONE_PILL[tone]}`}>
      {children}
    </span>
  );
}

export function Dot({ tone }: { tone: Tone }): JSX.Element {
  return <span className={`mt-1.5 h-2 w-2 flex-shrink-0 rounded-full ${TONE_DOT[tone]}`} />;
}

/** A small window-like panel with a title strip. */
export function Panel({ title, meta, children }: { title: string; meta?: string; children: ReactNode }): JSX.Element {
  return (
    <div className="overflow-hidden rounded-[var(--landing-radius)] border border-[var(--landing-border)] bg-[var(--landing-surface)] shadow-[var(--landing-shadow)]">
      <div className="flex items-center justify-between gap-3 border-b border-[var(--landing-border)] px-4 py-2.5">
        <span className="truncate text-xs font-semibold text-[var(--landing-text-primary)]">{title}</span>
        {meta && <span className="flex-shrink-0 text-[11px] text-[var(--landing-text-muted)]">{meta}</span>}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

/** A labelled arrow between two steps of a scene. */
export function Flow({ label }: { label: string }): JSX.Element {
  return (
    <div className="flex items-center gap-2 py-2 pl-4 text-[11px] font-medium text-[var(--landing-accent-light)]">
      <span className="text-[var(--landing-accent-text)]">&darr;</span>
      {label}
    </div>
  );
}

/** A stand-in for a photo: an inset box with a simple landscape glyph. */
export function Photo({ className }: { className: string }): JSX.Element {
  return (
    <div className={`flex items-center justify-center rounded-md border border-[var(--landing-border)] bg-[var(--landing-surface-alt)] ${className}`}>
      <svg className="h-6 w-6 text-[var(--landing-border)]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <circle cx="9" cy="10" r="1.5" />
        <path strokeLinejoin="round" d="m3 17 5-5 4 4 3-3 6 6" />
      </svg>
    </div>
  );
}

export const ROW ='flex items-start justify-between gap-3 py-2.5 text-sm';
export const DIVIDED = 'divide-y divide-[var(--landing-border)]';
export const PRIMARY = 'text-[var(--landing-text-primary)]';
export const MUTED = 'text-xs text-[var(--landing-text-muted)]';
export const INSET = 'rounded-md border border-[var(--landing-border)] bg-[var(--landing-surface-alt)]';
